import {
  useAcceptTos,
  useCurrentUserQuery,
  useRequestTosAcceptance,
  useTosAcceptanceQuery,
} from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Checkbox,
  Container,
  ExternalLink,
  FullDialog,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useOrgId } from '../useOrgId'
import { useLocation } from 'react-router'

/**
 * When mounted, this component ensures that the current organization has accepted the Terms of Service.
 * If the organization has not accepted the Terms of Service, the component will display a dialog with a button to review the document.
 */
export const EnsureTosAccepted = () => {
  const currentLocation = useLocation()
  const orgId = useOrgId()
  const { data: currentUserData, isLoading: isLoadingCurrentUser } = useCurrentUserQuery()
  const currentUser = currentUserData?.currentUser
  const { data: currentTosAcceptanceData, isFetching: isFetchingTosAcceptance } = useTosAcceptanceQuery({
    ownerId: orgId,
  })
  const { mutate: acceptTos } = useAcceptTos(orgId)
  const { mutate: requestTosAcceptance, isLoading: isRequestingTosAcceptance } =
    useRequestTosAcceptance(orgId)
  const [acceptEsign, setAcceptEsign] = React.useState<boolean | undefined>(undefined)
  const [acceptTerms, setAcceptTerms] = React.useState<boolean | undefined>(undefined)

  const acceptance = currentTosAcceptanceData?.organization?.acceptance // could be empty because it's loading or because the org hasn't completed contract signing
  const docUrl = acceptance?.signedDocument?.url
  const esignAccepted = Boolean(currentTosAcceptanceData?.organization?.esignAcceptance?.acceptedAt)
  const mustRequestAcceptance =
    acceptance &&
    currentUser &&
    (!acceptance.agreement.isMostRecent || acceptance.accepter.id !== currentUser.id)
  const isReadyToAccept =
    acceptance &&
    currentUser &&
    acceptance.agreement.isMostRecent &&
    acceptance.accepter.id === currentUser.id &&
    !acceptance.acceptedAt
  const tosAccepted = Boolean(acceptance?.acceptedAt)
  const enableAcceptance = (esignAccepted || acceptEsign) && acceptTerms

  React.useEffect(() => {
    if (!mustRequestAcceptance || !currentUser?.id) return
    requestTosAcceptance({ ownerId: orgId, accepterId: currentUser.id })
  }, [mustRequestAcceptance, orgId, currentUser?.id, requestTosAcceptance])

  if (currentLocation.pathname.includes('order-form')) return null
  if (tosAccepted || isFetchingTosAcceptance) return null
  if (isLoadingCurrentUser) return null
  if (!isRequestingTosAcceptance && !isReadyToAccept) return null

  return (
    <FullDialog
      title={<Typography variant="h2">Updates to Our Terms of Service</Typography>}
      open={true} // force open - only acceptance will close (by unmounting) the dialog
      actions={
        <Button
          variant="contained"
          color="primary"
          onClick={() => acceptance && acceptTos({ acceptanceId: acceptance.id, acceptEsign })}
          disabled={!enableAcceptance}
        >
          Accept
        </Button>
      }
    >
      <Container>
        <Box pb={2}>
          <Typography variant="body2" gutterBottom>
            Our Terms of Service have been updated to reflect service improvements.
          </Typography>
          <Typography variant="body2" gutterBottom fontWeight="bold">
            Highlights of the updates:
          </Typography>
          <Typography variant="body2" component="ul" gutterBottom>
            <li>Revised terms regarding connecting shift scheduling systems.</li>
            <li>
              Clearer billing terms, including notice and grace period details for avoidable surcharges.
            </li>
            <li>Revised onboarding policy.</li>
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Billing confirmation:</strong> Your Billing Amount is not changing. It remains capped at
            your payroll tax savings per pay period.
          </Typography>

          <Box mt={3}>
            <Checkbox
              color="primary"
              name="acceptTerms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              disabled={isRequestingTosAcceptance || !docUrl}
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <ExternalLink target="_blank" href={docUrl ?? ''}>
                    updated Terms of Service
                  </ExternalLink>
                </Typography>
              }
            />

            {!esignAccepted && (
              <Checkbox
                color="primary"
                name="acceptTermsAndEsign"
                checked={acceptEsign}
                onChange={(e) => setAcceptEsign(e.target.checked)}
                label={
                  <Typography variant="body2">
                    <FormattedMessage
                      id="agreement.esign_consent.label"
                      values={{
                        'esign-consent': () => (
                          <ExternalLink target="_blank" href="/docs/esign-consent">
                            <FormattedMessage id="enroll.register.legal_agreement.esign_disclosure_and_consent" />
                          </ExternalLink>
                        ),
                      }}
                    />
                  </Typography>
                }
              />
            )}
          </Box>
        </Box>
      </Container>
    </FullDialog>
  )
}
